import { Link, useNavigate } from '@tanstack/react-router';
import { COMMUNITY_TOPICS, topicLabel, type CommunityTopicKey } from '@zoxo/shared';
import { MessageCircle, Plus, Poll, Users } from '@/components/icons';

import { EmptyState } from '../../components/ui/empty-state';
import { PILL_TAB } from '../../components/ui/pill-tab';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useIntersection } from '../../hooks/use-intersection';
import { cn } from '../../lib/utils';
import { formatDate } from '../articles/format';
import { usePosts } from './use-posts';
import { ListTail } from '../../components/ui/list-tail';

/**
 * 커뮤니티 목록. 위에 게시판(소주제) 선택 상자 — 「전체」 + 여섯 게시판 — 오른쪽에 「+ 글쓰기」.
 * 목록은 공고·아티클과 같은 카드 리듬과 무한 스크롤.
 */
export function CommunityList({ topic }: { topic?: CommunityTopicKey }) {
  const navigate = useNavigate();
  return (
    <section>
      <h1 className="sr-only">커뮤니티</h1>
      <div className="mb-4 flex items-center justify-between gap-2">
        {/* 게시판 고르기 — 알약 줄이 길어 가로 스크롤이 생겼다. 지원사업 목록과 같은 shadcn Select. 기본 「전체」. */}
        <Select
          value={topic ?? 'all'}
          onValueChange={(v) =>
            void navigate({ to: '/posts', search: v === 'all' ? {} : { topic: v as CommunityTopicKey } })
          }
        >
          <SelectTrigger aria-label="게시판" className="w-[220px]">
            {/* 글자를 직접 준다 — Radix 는 선택값 글자를 브라우저에서만 채워 서버 HTML 의 상자가 빈다. */}
            <SelectValue>{topic ? topicLabel(topic) : '전체'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>게시판</SelectLabel>
              <SelectItem value="all">전체</SelectItem>
              {COMMUNITY_TOPICS.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Link
          to="/posts/create"
          search={topic ? { topic } : {}}
          className={cn(PILL_TAB, 'shrink-0 gap-1 bg-brand text-white hover:bg-brand-hover')}
        >
          <Plus aria-hidden="true" className="size-4" />
          글쓰기
        </Link>
      </div>
      <CommunityFeed topic={topic} />
    </section>
  );
}

/** 글 목록. `mine` 이면 내 글만(마이페이지 「내 글」) — 같은 카드, 같은 꼬리. */
export function CommunityFeed({ topic, mine = false }: { topic?: CommunityTopicKey; mine?: boolean }) {
  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = usePosts(topic, mine);
  const sentinel = useIntersection(() => void fetchNextPage(), hasNextPage && !isFetchingNextPage);
  if (isPending) return <p className="text-sm text-text-muted">불러오는 중…</p>;
  if (isError) return <p className="text-sm text-text-muted">글을 불러오지 못했어요.</p>;
  const posts = data.pages.flatMap((p) => p.data);
  if (posts.length === 0) {
    return (
      <EmptyState
        icon={<Users />}
        title={mine ? '아직 쓴 글이 없어요.' : topic ? `「${topicLabel(topic)}」에 아직 글이 없어요.` : '아직 글이 없어요.'}
        description="첫 글을 남겨 보세요."
      />
    );
  }
  return (
    <>
      <ul className="divide-y divide-border-subtle">
        {posts.map((p) => (
          <li key={p.id} className="py-4">
            {/* 모바일: 제목 줄 + 정보 줄(두 줄). sm 이상: 왼쪽 제목, 오른쪽 게시판 · 글쓴이 · 날짜(한 줄). */}
            <Link to="/posts/$id" params={{ id: p.id }} className="group flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
              <h2 className="line-clamp-2 min-w-0 text-sm font-bold leading-snug text-text group-hover:underline underline-offset-2">
                {p.title}
                {/* 설문이 붙어 있으면 투표 아이콘 — 댓글 수 왼쪽. */}
                {p.hasSurvey ? (
                  <Poll className="ml-1.5 inline size-3.5 align-middle text-brand" aria-label="설문 있음" />
                ) : null}
                {/* 댓글 수 — 제목 바로 뒤 「(N)」. 없으면 안 보인다. */}
                {p.commentCount > 0 ? (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-sm font-medium text-brand">
                    <MessageCircle className="size-3.5" aria-hidden="true" />
                    <span aria-label={`댓글 ${p.commentCount}개`}>({p.commentCount})</span>
                  </span>
                ) : null}
              </h2>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-subtle sm:shrink-0 sm:flex-nowrap sm:whitespace-nowrap">
                {p.topic ? (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
                    {topicLabel(p.topic)}
                  </span>
                ) : null}
                <span className="font-medium text-text-muted">{p.author.nickname}</span>
                <span aria-hidden="true">·</span>
                <span>{formatDate(p.createdAt)}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <div ref={sentinel} aria-hidden className="h-px" />
      <ListTail fetching={isFetchingNextPage} done={!hasNextPage} />
    </>
  );
}
